import React from "react";

// Lightweight CSS-based drop-in replacement for the Motion library.
// It renders plain DOM elements (no React effects), so there is no effect
// cleanup that can ever throw "destroy is not a function", and elements
// default to visible (opacity:1) so pages can never render blank.

const MOTION_PROPS = new Set([
    "initial", "animate", "exit", "transition", "variants", "custom",
    "whileHover", "whileTap", "whileFocus", "whileInView", "whileDrag",
    "viewport", "onViewportEnter", "onViewportLeave",
    "layout", "layoutId", "layoutDependency", "layoutScroll", "layoutRoot",
    "drag", "dragConstraints", "dragElastic", "dragMomentum", "dragControls", "dragListener",
    "onAnimationStart", "onAnimationComplete", "onUpdate", "onDrag", "onDragStart", "onDragEnd",
]);

const TRANSFORM_KEYS = new Set([
    "x", "y", "z", "scale", "scaleX", "scaleY", "rotate", "rotateX", "rotateY",
    "rotateZ", "skew", "skewX", "skewY", "originX", "originY", "perspective",
]);

function isMotionValue(v) {
    return v && typeof v === "object" && typeof v.get === "function";
}

function clean(props) {
    const out = {};
    for (const k in props) {
        if (MOTION_PROPS.has(k)) continue;
        if (k === "style" && props.style && typeof props.style === "object") {
            const s = {};
            for (const sk in props.style) {
                if (TRANSFORM_KEYS.has(sk)) continue;
                const v = props.style[sk];
                if (isMotionValue(v)) continue;
                s[sk] = v;
            }
            out.style = s;
            continue;
        }
        out[k] = props[k];
    }
    return out;
}

const cache = {};
function makeComponent(tag) {
    if (cache[tag]) return cache[tag];
    const Comp = React.forwardRef(function MotionShim({ className = "", ...rest }, ref) {
        const cleaned = clean(rest);
        const cls = (className ? className + " " : "") + "sp-anim-in";
        return React.createElement(tag, { ref, className: cls, ...cleaned });
    });
    Comp.displayName = `motion.${tag}`;
    cache[tag] = Comp;
    return Comp;
}

export const motion = new Proxy(
    {},
    { get: (_t, tag) => makeComponent(typeof tag === "string" ? tag : "div") }
);

export function AnimatePresence({ children }) {
    return React.createElement(React.Fragment, null, children);
}

// No-op hook shims (return static, safe values)
const noopMV = { get: () => 0, set: () => {}, on: () => () => {}, destroy: () => {} };
export function useScroll() {
    return { scrollYProgress: noopMV, scrollY: noopMV, scrollX: noopMV, scrollXProgress: noopMV };
}
export function useTransform() {
    return noopMV;
}
export function useMotionValue(v) {
    return { get: () => v, set: () => {}, on: () => () => {}, destroy: () => {} };
}
export function useInView() {
    return true;
}
export function useMotionTemplate() {
    return "";
}
